import {
  Activity,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowLeftRight,
  ArrowUpRight,
  BadgeCheck,
  Bell,
  BookOpen,
  Bookmark,
  Bot,
  Bug,
  Building2,
  CalendarDays,
  ChartColumn,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsUpDown,
  CircleAlert,
  CircleArrowUp,
  CircleCheck,
  CircleHelp,
  CirclePlus,
  CircleX,
  Clock,
  Columns3,
  Copy,
  CreditCard,
  Database,
  Download,
  EllipsisVertical,
  ExternalLink,
  Eye,
  Filter,
  Fingerprint,
  Flame,
  Folder,
  FolderInput,
  Gauge,
  Gem,
  Gift,
  Globe,
  GraduationCap,
  Hash,
  Heart,
  Inbox,
  Info,
  Layers,
  LayoutDashboard,
  LifeBuoy,
  Lightbulb,
  Link,
  LoaderCircle,
  Lock,
  LogOut,
  Mail,
  MapPin,
  Menu,
  MessageSquare,
  Minus,
  Monitor,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Pause,
  Pencil,
  Phone,
  Play,
  Plus,
  Puzzle,
  Radar,
  RefreshCw,
  Rocket,
  Search,
  Send,
  Server,
  Settings,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  Sprout,
  Square,
  Star,
  Sun,
  Tag,
  Tags,
  Target,
  Trash2,
  TriangleAlert,
  Upload,
  User,
  Users,
  Video,
  WandSparkles,
  Workflow,
  X,
  Zap,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

// A closed icon set. A feature names an icon; it never imports an icon
// library. A missing icon is added here, once.

const ICONS = {
  activity: Activity,
  "alert-circle": CircleAlert,
  "alert-triangle": TriangleAlert,
  "arrow-down": ArrowDown,
  "arrow-left": ArrowLeft,
  "arrow-right": ArrowRight,
  "arrow-up": ArrowUp,
  "arrow-up-right": ArrowUpRight,
  "arrow-up-circle": CircleArrowUp,
  "badge-check": BadgeCheck,
  bell: Bell,
  book: BookOpen,
  bookmark: Bookmark,
  bot: Bot,
  bug: Bug,
  building: Building2,
  calendar: CalendarDays,
  chart: ChartColumn,
  check: Check,
  "check-circle": CircleCheck,
  "chevron-down": ChevronDown,
  "chevron-left": ChevronLeft,
  "chevron-right": ChevronRight,
  "chevron-up": ChevronUp,
  "chevrons-up-down": ChevronsUpDown,
  clock: Clock,
  columns: Columns3,
  copy: Copy,
  "credit-card": CreditCard,
  dashboard: LayoutDashboard,
  database: Database,
  download: Download,
  edit: Pencil,
  "external-link": ExternalLink,
  eye: Eye,
  filter: Filter,
  fingerprint: Fingerprint,
  flame: Flame,
  folder: Folder,
  "folder-input": FolderInput,
  gauge: Gauge,
  gem: Gem,
  gift: Gift,
  globe: Globe,
  "graduation-cap": GraduationCap,
  hash: Hash,
  heart: Heart,
  help: CircleHelp,
  inbox: Inbox,
  info: Info,
  layers: Layers,
  "life-buoy": LifeBuoy,
  lightbulb: Lightbulb,
  link: Link,
  loader: LoaderCircle,
  lock: Lock,
  "log-out": LogOut,
  mail: Mail,
  "map-pin": MapPin,
  menu: Menu,
  message: MessageSquare,
  minus: Minus,
  monitor: Monitor,
  moon: Moon,
  more: EllipsisVertical,
  "panel-close": PanelLeftClose,
  "panel-open": PanelLeftOpen,
  pause: Pause,
  phone: Phone,
  play: Play,
  plus: Plus,
  "plus-circle": CirclePlus,
  puzzle: Puzzle,
  radar: Radar,
  refresh: RefreshCw,
  rocket: Rocket,
  search: Search,
  send: Send,
  server: Server,
  settings: Settings,
  "shield-check": ShieldCheck,
  "shopping-bag": ShoppingBag,
  sparkles: Sparkles,
  sprout: Sprout,
  star: Star,
  stop: Square,
  sun: Sun,
  swap: ArrowLeftRight,
  tag: Tag,
  tags: Tags,
  target: Target,
  trash: Trash2,
  upload: Upload,
  user: User,
  users: Users,
  video: Video,
  wand: WandSparkles,
  workflow: Workflow,
  x: X,
  "x-circle": CircleX,
  zap: Zap,
} as const satisfies Record<string, LucideIcon>;

export type IconName = keyof typeof ICONS;

export const ICON_NAMES = Object.keys(ICONS) as readonly IconName[];

export function isIconName(value: string): value is IconName {
  return Object.hasOwn(ICONS, value);
}

export function Icon({
  name,
  label,
  spin,
}: {
  readonly name: IconName;
  readonly label?: string;
  readonly spin?: boolean;
}) {
  const Glyph = ICONS[name];
  return (
    <Glyph
      data-icon={name}
      aria-hidden={label ? undefined : true}
      aria-label={label}
      role={label ? "img" : undefined}
      className={spin ? "animate-spin" : undefined}
      strokeWidth={1.75}
    />
  );
}
